import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useCatalog } from "@/lib/useCatalog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PageLoader } from "@/components/layout/RequireAuth";
import { VerificationSection } from "@/components/VerificationSection";

const ChipToggle = ({ items, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it) => {
      const on = value.includes(it.slug);
      return (
        <button key={it.slug} type="button" data-testid={`${testPrefix}-${it.slug}`}
          onClick={() => onChange(on ? value.filter((v) => v !== it.slug) : [...value, it.slug])}
          className={cn("rounded-full border px-3.5 py-1.5 text-sm transition-[border-color,background-color] duration-150",
            on ? "border-teal-600 bg-teal-700 text-white" : "border-stone-200 bg-white text-stone-700 hover:border-stone-400")}>
          {it.name}
        </button>
      );
    })}
  </div>
);

const Section = ({ title, desc, children }) => (
  <div className="grid gap-4 border-b border-stone-100 py-7 last:border-0 md:grid-cols-[220px_1fr] md:gap-8">
    <div>
      <p className="text-sm font-bold text-stone-900">{title}</p>
      {desc && <p className="mt-1 text-xs leading-relaxed text-stone-500">{desc}</p>}
    </div>
    <div>{children}</div>
  </div>
);

export default function ProviderEditor() {
  const { refreshUser } = useAuth();
  const { groups, cities } = useCatalog();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/provider/profile").then((r) => setForm(r.data));
  }, []);

  if (!form) return <PageLoader />;
  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { business_name, logo_path, subcategories, cities, description, whatsapp, portfolio, bank_name, bank_account_number, bank_account_name } = form;
      const payload = { business_name, logo_path, subcategories, cities, description, whatsapp, portfolio, experience_years: Number(form.experience_years) || 0,
        bank_name: bank_name || "", bank_account_number: bank_account_number || "", bank_account_name: bank_account_name || "" };
      const { data } = await api.put("/provider/profile", payload);
      setForm(data);
      await refreshUser();
      toast.success("Profil usaha tersimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-page max-w-5xl py-10 lg:py-14" data-testid="provider-editor-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow">Penyedia</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Profil Usaha</h1>
          <p className="mt-2 text-stone-500">Lengkapi profil agar Anda menerima permintaan yang sesuai.</p>
        </div>
        <Link to={`/penyedia/${form.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900" data-testid="view-public-profile-link">
          Lihat profil publik <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
      <form onSubmit={save} className="card-soft mt-8 px-6 sm:px-8">
        <Section title="Logo" desc="Format JPG/PNG, maks. 5 MB.">
          <PhotoUploader value={form.logo_path ? [form.logo_path] : []} onChange={(v) => set("logo_path")(v[0] || null)} max={1} testId="logo-uploader" />
        </Section>
        <Section title="Nama usaha">
          <Input value={form.business_name} onChange={set("business_name")} className="h-11 rounded-xl" data-testid="editor-business-name-input" />
        </Section>
        <Section title="Kategori layanan" desc="Anda hanya menerima permintaan di subkategori ini.">
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.slug}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">{g.name}</p>
                <ChipToggle items={g.subcategories} value={form.subcategories} onChange={set("subcategories")} testPrefix="editor-subcategory" />
              </div>
            ))}
          </div>
        </Section>
        <Section title="Area layanan" desc="Kota tempat Anda bisa bekerja.">
          <ChipToggle items={cities} value={form.cities} onChange={set("cities")} testPrefix="editor-city" />
        </Section>
        <Section title="Deskripsi usaha" desc="Minimal 20 karakter.">
          <Textarea rows={5} value={form.description} onChange={set("description")} className="rounded-xl" data-testid="editor-description-input" />
        </Section>
        <Section title="Pengalaman & kontak" desc="Nomor WhatsApp tidak ditampilkan publik.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Pengalaman (tahun)</Label>
              <Input type="number" min={0} value={form.experience_years} onChange={set("experience_years")} className="h-11 rounded-xl" data-testid="editor-experience-input" />
            </div>
            <div className="space-y-2">
              <Label>Nomor WhatsApp</Label>
              <Input type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="08xxxxxxxxxx" className="h-11 rounded-xl" data-testid="editor-whatsapp-input" />
            </div>
          </div>
        </Section>
        <Section title="Rekening bank" desc="Untuk pencairan dana. Tidak ditampilkan publik.">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2"><Label>Nama bank</Label><Input value={form.bank_name || ""} onChange={set("bank_name")} placeholder="mis. BCA" className="h-11 rounded-xl" data-testid="editor-bank-name-input" /></div>
            <div className="space-y-2"><Label>Nomor rekening</Label><Input value={form.bank_account_number || ""} onChange={set("bank_account_number")} className="h-11 rounded-xl" data-testid="editor-bank-number-input" /></div>
            <div className="space-y-2"><Label>Nama pemilik rekening</Label><Input value={form.bank_account_name || ""} onChange={set("bank_account_name")} className="h-11 rounded-xl" data-testid="editor-bank-holder-input" /></div>
          </div>
        </Section>
        <Section title="Portofolio" desc="Hingga 12 foto hasil kerja terbaik Anda.">
          <PhotoUploader value={form.portfolio} onChange={set("portfolio")} max={12} testId="portfolio-uploader" />
        </Section>
        <div className="flex justify-end py-6">
          <Button type="submit" disabled={saving} className="h-11 rounded-xl bg-teal-700 px-8 font-semibold hover:bg-teal-800" data-testid="editor-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Profil
          </Button>
        </div>
      </form>
      <VerificationSection profile={form} onChange={(verification) => setForm({ ...form, verification })} />
    </div>
  );
}
